import { makeAutoObservable, runInAction } from 'mobx'
import { notification } from 'antd'
import { openApi } from '../../helpers/request'
import i from '../../translations/i'

export type CategoryStatus = 'all' | 'active' | 'hidden'

const DEFAULT_LIMIT = 10
const SEARCH_DELAY = 500

export interface CategoryTableOptions {
    // Родительская категория; 0 — верхний уровень каталога
    parentId: number
    // Весь ответ списка — хлебные крошки и счётчик товаров без категории
    onLoaded?: (result: any) => void
}

// Куда ведёт категория: с подкатегориями — в них, без — в её товары
export const categoryUrl = (category: any) => category.subcategories
    ? `/shop/catalog/category/${category.id}`
    : `/shop/products/${category.id}`

// Состояние таблицы категорий каталога (CategoryTable)
export class CategoryTableController {

    readonly options: CategoryTableOptions

    list: any[] = []
    loading = false
    total = 0
    page = 1
    limit = DEFAULT_LIMIT

    searchText = ''
    status: CategoryStatus = 'all'

    selectedIds: number[] = []
    bulkLoading = false
    busyIds: number[] = []

    private searchTimeout: ReturnType<typeof setTimeout> | null = null

    constructor(options: CategoryTableOptions) {
        this.options = options
        makeAutoObservable<CategoryTableController, 'searchTimeout'>(this, { options: false, searchTimeout: false })
    }

    get filtered(): boolean {
        return !!(this.searchText || this.status !== 'all')
    }

    // Порядок в каталоге меняется стрелками, только пока список не сужен поиском или статусом
    get canMove(): boolean {
        return !this.filtered
    }

    load() {
        this.readUrl()
        this.getList()
    }

    getList(page: number | null = null) {
        this.cancelSearch()

        if (page) this.page = page

        const params = [`limit=${this.limit}`, `page=${this.page}`, 'with[]=image']
        if (this.searchText) params.push(`filter[name]=${encodeURIComponent(this.searchText)}`)
        if (this.status === 'active') params.push('filter[view]=1')
        if (this.status === 'hidden') params.push('filter[view]=0')

        this.loading = true

        openApi('get', `/category/list/${this.options.parentId}?${params.join('&')}`)
            .then(result => {
                runInAction(() => {
                    this.list = result.data ?? []
                    this.total = result.total ?? 0
                })
                this.options.onLoaded?.(result)
            })
            .catch(error => console.log(error?.response))
            .finally(() => runInAction(() => this.loading = false))
    }

    setSearch(text: string) {
        this.searchText = text
        this.selectedIds = []
        this.cancelSearch()
        this.searchTimeout = setTimeout(() => this.getList(1), SEARCH_DELAY)
    }

    setStatus(status: CategoryStatus) {
        this.status = status
        this.reload()
    }

    setPagination(page: number, limit: number) {
        if (limit !== this.limit) {
            this.limit = limit
            this.reload()
        } else if (page !== this.page) {
            this.selectedIds = []
            this.getList(page)
        }
    }

    resetFilters() {
        this.searchText = ''
        this.status = 'all'
        this.reload()
    }

    private reload() {
        this.selectedIds = []
        this.getList(1)
    }

    private cancelSearch() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = null
        }
    }

    // ---------- Действия ----------

    isBusy(id: number) {
        return this.busyIds.includes(id)
    }

    private setBusy(id: number, busy: boolean) {
        this.busyIds = busy ? [...this.busyIds, id] : this.busyIds.filter(busyId => busyId !== id)
    }

    private requestActive(id: number, view: boolean) {
        const data = new FormData()
        data.append('view', `${+ view}`)
        return openApi('post', `/category/${id}`, data)
    }

    // Показ в каталоге: переключатель меняется сразу; при ошибке возвращается как был
    setActive(id: number, view: boolean) {
        const category = this.list.find((el: any) => el.id === id)
        const previous = category?.view
        if (category) category.view = +view

        this.setBusy(id, true)

        this.requestActive(id, view)
            .then(() => this.getList())
            .catch(() => runInAction(() => {
                if (category) category.view = previous
                notification.error({ message: i.t('def:errors.error'), description: i.t('def:errors.somethingWentWrong') })
            }))
            .finally(() => runInAction(() => this.setBusy(id, false)))
    }

    move(id: number, action: 'up' | 'down') {
        if (!this.canMove) return

        const data = new FormData()
        data.append('action', action)
        data.append('category_id', `${id}`)
        data.append('parent_id', `${this.options.parentId}`)

        this.setBusy(id, true)

        openApi('post', '/shop/category/position', data)
            .then(() => this.getList())
            .catch(() => notification.error({ message: i.t('def:errors.error'), description: i.t('def:errors.somethingWentWrong') }))
            .finally(() => runInAction(() => this.setBusy(id, false)))
    }

    setSelectedIds(ids: number[]) {
        this.selectedIds = ids
    }

    setActiveSelected(view: boolean) {
        const ids = [...this.selectedIds]
        if (!ids.length) return

        this.bulkLoading = true

        Promise.allSettled(ids.map(id => this.requestActive(id, view)))
            .then(results => {
                const failed = results.filter(result => result.status === 'rejected').length

                if (failed) {
                    notification.error({ message: i.t('def:errors.error'), description: i.t('categoryCards:bulkFailed', { count: failed }) })
                } else {
                    notification.success({
                        message: i.t('def:success.complete'),
                        description: i.t(view ? 'categoryCards:bulkShown' : 'categoryCards:bulkHidden', { count: ids.length }),
                    })
                }

                runInAction(() => this.selectedIds = [])
                this.getList()
            })
            .finally(() => runInAction(() => this.bulkLoading = false))
    }

    deleteSelected() {
        const ids = [...this.selectedIds]
        if (!ids.length) return

        const data = new FormData()
        ids.forEach(id => data.append('categories[]', `${id}`))

        this.bulkLoading = true

        openApi('post', '/category/delete', data)
            .then(() => {
                runInAction(() => this.selectedIds = [])
                this.getList()
            })
            .catch(() => notification.error({ message: i.t('def:errors.error'), description: i.t('def:errors.somethingWentWrong') }))
            .finally(() => runInAction(() => this.bulkLoading = false))
    }

    // ---------- Адрес страницы ----------

    get url(): string {
        const params = new URLSearchParams()
        if (this.searchText) params.set('q', this.searchText)
        if (this.status !== 'all') params.set('status', this.status)
        if (this.page > 1) params.set('page', `${this.page}`)
        if (this.limit !== DEFAULT_LIMIT) params.set('limit', `${this.limit}`)

        const query = params.toString()
        return `${window.location.pathname}${query ? `?${query}` : ''}`
    }

    private readUrl() {
        const params = new URLSearchParams(window.location.search)

        this.searchText = params.get('q') ?? ''

        const status = params.get('status')
        this.status = status === 'active' || status === 'hidden' ? status : 'all'

        this.page = Number(params.get('page')) || 1
        this.limit = Number(params.get('limit')) || DEFAULT_LIMIT
    }
}
